<script setup lang="ts">
import { nextTick, onBeforeUnmount, useTemplateRef } from 'vue';

interface Props {
  displayCode: string;
  error: string;
  isLoading: boolean;
  loadingLabel: string;
  rawInput: string;
  subtitle: string;
  title: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  input: [value: string];
  keydown: [event: KeyboardEvent];
}>();

const inputRef = useTemplateRef<HTMLInputElement>('inputRef');
let focusInterval: ReturnType<typeof setInterval> | null = null;

function focusInput(): void {
  inputRef.value?.focus({ preventScroll: true });
}

function startFocusManagement(): void {
  void nextTick().then(focusInput);

  if (focusInterval) {
    clearInterval(focusInterval);
  }
  focusInterval = setInterval(() => {
    if (!props.isLoading && document.activeElement !== inputRef.value) {
      focusInput();
    }
  }, 500);
}

function stopFocusManagement(): void {
  if (focusInterval) {
    clearInterval(focusInterval);
    focusInterval = null;
  }
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onInput(event: Event): void {
  emit('input', (event.target as HTMLInputElement).value);
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onKeydown(event: KeyboardEvent): void {
  emit('keydown', event);
}

onBeforeUnmount(stopFocusManagement);

defineExpose({ focusInput, startFocusManagement, stopFocusManagement });
</script>

<template>
  <div
    class="GoPhotoScan"
    data-testid="gophoto-scan-root"
    @click="focusInput"
  >
    <div class="GoPhotoScan__icon">
      <svg
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        viewBox="0 0 24 24"
      >
        <rect height="7" width="7" x="3" y="3"/>
        <rect height="7" width="7" x="14" y="3"/>
        <rect height="7" width="7" x="3" y="14"/>
        <rect height="7" width="7" x="14" y="14"/>
      </svg>
    </div>

    <h1 class="GoPhotoScan__title kiosk-title">
      {{ title }}
    </h1>
    <p class="GoPhotoScan__subtitle">
      {{ subtitle }}
    </p>

    <div class="GoPhotoScan__display" data-testid="gophoto-scan-display">
      <span
        v-for="index in 6"
        :key="index"
        class="GoPhotoScan__char"
        :class="{ filled: displayCode.length >= index }"
        data-testid="gophoto-scan-char"
      >
        {{ displayCode.length >= index ? displayCode[index - 1] : '' }}
      </span>
    </div>

    <div
      v-if="error"
      class="GoPhotoScan__error"
      data-testid="gophoto-scan-error"
    >
      {{ error }}
    </div>

    <div
      v-if="isLoading"
      class="GoPhotoScan__loading"
      data-testid="gophoto-scan-loading"
    >
      <span class="GoPhotoScan__spinner"/>
      <span>{{ loadingLabel }}</span>
    </div>

    <input
      ref="inputRef"
      autocomplete="off"
      autocapitalize="characters"
      autocorrect="off"
      class="GoPhotoScan__input"
      data-testid="gophoto-scan-input"
      maxlength="200"
      type="text"
      :value="rawInput"
      @input="onInput"
      @keydown="onKeydown"
    >
  </div>
</template>

<style scoped>
.GoPhotoScan {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
  cursor: pointer;
}

.GoPhotoScan__icon {
  width: 120px;
  height: 120px;
  margin-bottom: 2rem;
  opacity: 0.8;
  animation: pulse 2s ease-in-out infinite;
}

.GoPhotoScan__icon svg {
  width: 100%;
  height: 100%;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
}

.GoPhotoScan__title {
  margin-bottom: 1rem;
}

.GoPhotoScan__subtitle {
  font-size: 1.25rem;
  opacity: 0.7;
  margin-bottom: 3rem;
}

.GoPhotoScan__display {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
}

.GoPhotoScan__char {
  width: 4rem;
  height: 5rem;
  border: 3px solid
    var(--Kiosk--button_background_color, rgba(255, 255, 255, 0.3));
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  font-weight: 600;
  font-family: monospace;
  background: rgba(255, 255, 255, 0.05);
  transition: all 0.2s ease;
  text-transform: uppercase;
}

.GoPhotoScan__char.filled {
  border-color: var(
    --Kiosk--button_background_color,
    rgba(255, 255, 255, 0.7)
  );
  background: rgba(255, 255, 255, 0.15);
}

.GoPhotoScan__error {
  color: #ff4444;
  font-size: 1.1rem;
  margin-bottom: 1rem;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 68, 68, 0.1);
  border-radius: 0.5rem;
}

.GoPhotoScan__loading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.1rem;
  opacity: 0.8;
}

.GoPhotoScan__spinner {
  width: 1.5rem;
  height: 1.5rem;
  border: 2px solid transparent;
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.GoPhotoScan__input {
  position: absolute;
  left: -9999px;
  opacity: 0;
  pointer-events: none;
}

@media (min-height: 1000px) {
  .GoPhotoScan__icon {
    width: 180px;
    height: 180px;
    margin-bottom: 3rem;
  }

  .GoPhotoScan__subtitle {
    font-size: 1.75rem;
    margin-bottom: 4rem;
  }

  .GoPhotoScan__display {
    gap: 1.5rem;
    margin-bottom: 3rem;
  }

  .GoPhotoScan__char {
    width: 5rem;
    height: 6.5rem;
    font-size: 3rem;
  }
}
</style>
